import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircleNotch, GoogleLogo, PlugsConnected, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const ERRORS = {
  access_denied: "Google access wasn't granted",
  send_scope_not_granted: "Please tick the \u201cSend email on your behalf\u201d permission when connecting",
  invalid_state: "That connection link expired — please try again",
  expired: "That connection link expired — please try again",
};

// Shows which Google mailbox Flash Reports are sent from, with connect / disconnect.
export function MailConnectionCard({ onStatus }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/mail/status").then(({ data }) => { setSt(data); onStatus?.(data); }), [onStatus]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    if (q.get("mail") === "connected") toast.success(`Emails will now send from ${q.get("mail_email")}`);
    if (q.get("mail_error")) toast.error(ERRORS[q.get("mail_error")] || "Couldn't connect Google — please try again");
    if (q.get("mail") || q.get("mail_error")) {
      ["mail", "mail_email", "mail_error"].forEach((k) => q.delete(k));
      navigate({ search: q.toString() }, { replace: true });
    }
    load().catch(() => setSt({ status: "disconnected" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mail/google/authorize", { return_to: location.pathname });
      // Google refuses to load inside frames (e.g. an embedded preview), so break out to a new tab there.
      if (window.top !== window.self) {
        window.open(data.authorize_url, "_blank", "noopener");
        setBusy(false);
      } else {
        window.location.href = data.authorize_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the Google connection");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(`Stop sending Flash Reports from ${st.email}?`)) return;
    await api.post("/mail/google/disconnect");
    toast.success("Google mailbox disconnected");
    load();
  };

  if (!st) return <div className="h-10" data-testid="mail-connection-loading" />;

  if (st.status === "connected") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-[#D1FAE5] bg-[#ECFDF5] px-3 py-2 text-xs text-[#065F46]" data-testid="mail-connected">
        <PlugsConnected size={15} className="shrink-0" />
        <span className="min-w-0 flex-1 truncate">Sending from <b className="font-mono" data-testid="mail-sender-email">{st.email}</b></span>
        <button type="button" onClick={disconnect} className="underline hover:text-[#064E3B]" data-testid="mail-disconnect-btn">Disconnect</button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 space-y-2" data-testid="mail-not-connected">
      <p className="flex gap-2">
        <Warning size={15} className="shrink-0" />
        {st.status === "expired" ? "Google access has expired. Reconnect the mailbox to send emails."
          : st.app_configured ? "Connect the practice Google mailbox to send Flash Report emails. FLASH can only send emails, not read your inbox."
            : "Google sign-in isn't set up on the server yet (Google Client ID and Secret are needed)."}
      </p>
      {st.app_configured && (
        <Button size="sm" onClick={connect} disabled={busy} className="h-8 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs" data-testid="mail-connect-btn">
          {busy ? <CircleNotch size={13} className="mr-1.5 animate-spin" /> : <GoogleLogo size={13} weight="bold" className="mr-1.5" />}
          {st.status === "expired" ? "Reconnect Google" : "Connect Google mailbox"}
        </Button>
      )}
    </div>
  );
}
